import { Injectable } from '@angular/core';
import {
  AuthenticationService,
  AuthenticationStatus,
  ConfigService,
  ErrorService,
  ErrorUnknown,
  LoggingService,
  LoginType,
  PITEventService,
  UtilsMisc,
  UtilsString,
} from '@pit/core';
import { IDtoUser } from '@pit/frontend-model';
import { BehaviorSubject, filter } from 'rxjs';
import { PinItTrackFrontendDataServicesAccessor } from '../../../module.data/accessor/implementations/pin.it.track.frontend.data.services.accessor';
import { EventApplication, EventUI } from '../../../module.event/pin.it.track.frontend.events';
import { AppMode } from '../interfaces/pin.it.track.frontend.application.service.interface';
import { IPinItTrackFrontendAuthenticationService } from '../interfaces/pin.it.track.frontend.authentication.service.interface';
import { Store } from '@ngrx/store';
import {
  MainState,
  signedInUserActions,
  signedInUserSelectors,
} from '../../../module.store/signed-in-user';

@Injectable({
  providedIn: 'root',
})
export class PinItTrackFrontendAuthenticationService
  extends AuthenticationService
  implements IPinItTrackFrontendAuthenticationService
{
  private _currentUser: IDtoUser = null;
  private _currentUser$: BehaviorSubject<IDtoUser> = new BehaviorSubject<IDtoUser>(null);

  constructor(
    configurationService: ConfigService,
    loggingService: LoggingService,
    private dataServicesAccessor: PinItTrackFrontendDataServicesAccessor,
    private eventService: PITEventService,
    private errorService: ErrorService,
    private store: Store<MainState>
  ) {
    super(configurationService, loggingService);

    this.authenticationStatus = AuthenticationStatus.LoggedOut;

    this.eventService.subscribeForEvent('auth', this.handleEventAuthAsync.bind(this));

    this.loggingService.debug('PinItTrackFrontendAuthenticationService --> constructor()');

    this.store
      .select(signedInUserSelectors.selectSignedInUser)
      .pipe(filter(Boolean))
      .subscribe((user) => {
        this._currentUser = user;
        this.currentUser$.next(user);

        this.authenticationStatus = AuthenticationStatus.LoggedIn;
        this.authenticationStatus$.next(AuthenticationStatus.LoggedIn);
      });
  }

  public get currentUser(): IDtoUser {
    return this._currentUser;
  }

  public get currentUser$(): BehaviorSubject<IDtoUser> {
    return this._currentUser$;
  }

  protected override async doLoginUserNameAsync(
    mandator: string,
    userName: string
  ): Promise<AuthenticationStatus> {
    let result: AuthenticationStatus = AuthenticationStatus.LoggedOut;

    const uName: string =
      await this.dataServicesAccessor.authenticationDataService.authenticateUserNameAsync(
        mandator,
        userName
      );
    if (UtilsString.stringIsNullOrEmpty(uName) === false) {
      result = AuthenticationStatus.InLoginUserChecked;
    }

    return result;
  }

  protected override async doLoginAsync(
    loginType: LoginType,
    mandator: string,
    userName: string,
    password: string
  ): Promise<any> {
    switch (loginType) {
      case LoginType.Biometric: {
        break;
      }
      case LoginType.Credentials: {
        if (
          UtilsString.stringIsNullOrEmpty(mandator) === false &&
          UtilsString.stringIsNullOrEmpty(userName) === false
        ) {
          if (this.authenticationStatus === AuthenticationStatus.LoggedOut) {
            try {
              if (
                UtilsString.stringIsNullOrEmpty(password) === false &&
                userName.toLowerCase() === 'gast' &&
                password.toLowerCase() === 'gast'
              ) {
                this.authenticationStatus = AuthenticationStatus.LoggedIn;
                this.eventService.sendEvent(EventApplication.AppInit, {
                  mode: AppMode.Guest,
                });
              } else {
                this.authenticationStatus = await this.loginUserNameAsync(mandator, userName);

                if (this.authenticationStatus === AuthenticationStatus.InLoginUserChecked) {
                  this.authenticationStatus = AuthenticationStatus.InLoginCheckPassword;
                  const user: IDtoUser =
                    await this.dataServicesAccessor.authenticationDataService.authenticateUserAsync(
                      mandator,
                      userName,
                      password
                    );
                  if (UtilsMisc.isNullOrUndefined(user) === false) {
                    const localStorageUser: IDtoUser = {
                      ...user.toJSON(),
                      isAdmin: user.isAdmin,
                      isAdminSuper: user.isAdminSuper,
                    };

                    this.setCurrentUser(user);
                    this.store.dispatch(signedInUserActions.login({ user: localStorageUser }));
                    if (user.mandator.useLDAP !== true && user.passwordResetRequested === true) {
                      this.authenticationStatus = AuthenticationStatus.LoggedOut;
                      this.eventService.sendEvent(EventUI.NavigateToPasswordReset);
                    } else {
                      this.authenticationStatus = AuthenticationStatus.LoggedIn;
                      this.eventService.sendEvent(EventApplication.AppInit, {
                        mode: user.isAdmin === true ? AppMode.Admin : AppMode.User,
                      });
                    }
                  } else {
                    this.authenticationStatus = AuthenticationStatus.LoggedOut;
                    this.store.dispatch(signedInUserActions.logout());
                  }
                }
              }
            } catch (error) {
              this.authenticationStatus = AuthenticationStatus.LoggedOut;
              this.errorService.handleErrorAsync(error, true);
            }
          }
        }
        break;
      }
      case LoginType.PasswordReset: {
        if (
          UtilsString.stringIsNullOrEmpty(mandator) === false &&
          UtilsString.stringIsNullOrEmpty(password) === false
        ) {
          if (this.authenticationStatus === AuthenticationStatus.LoggedOut) {
            try {
              this.authenticationStatus = AuthenticationStatus.InLoginUserChecked;
              const user: IDtoUser =
                await this.dataServicesAccessor.authenticationDataService.resetPasswordForUserAsync(
                  mandator,
                  this.currentUser.userName,
                  password
                );
              if (UtilsMisc.isNullOrUndefined(user) === false) {
                this.setCurrentUser(user);
                this.authenticationStatus = AuthenticationStatus.LoggedIn;
                this.eventService.sendEvent(EventApplication.AppInit, {
                  mode: user.isAdmin === true ? AppMode.Admin : AppMode.User,
                });
              } else {
                this.authenticationStatus = AuthenticationStatus.LoggedOut;
                this.eventService.sendEvent(EventUI.NavigateToLogout);
              }
            } catch (error) {
              this.authenticationStatus = AuthenticationStatus.LoggedOut;
              this.errorService.handleErrorAsync(error, true);
            }
          }
        }
        break;
      }
    }
    return null;
  }

  protected override async doLogoutAsync(userId: any): Promise<any> {
    return null;
  }

  private async setCurrentUser(value: IDtoUser): Promise<void> {
    this._currentUser = value;
    this.currentUser$.next(this.currentUser);
    this.configurationService.configAuth.userName =
      UtilsMisc.isNullOrUndefined(this.currentUser) === false ? this.currentUser.userName : null;
  }

  private async handleEventAuthAsync(authData: any): Promise<void> {
    switch (authData.type) {
      case 'login.credentials.start': {
        this.authenticationStatus = AuthenticationStatus.LoggedOut;
        await this.setCurrentUser(null);
        this.eventService.sendEvent('navigate', 'auth/login');
        break;
      }
      case 'login.credentials': {
        await this.loginAsync(
          LoginType.Credentials,
          authData.mandator,
          authData.userName,
          authData.password
        );
        break;
      }
      case 'login.password.reset.start': {
        this.authenticationStatus = AuthenticationStatus.LoggedOut;
        //await this.setCurrentUser(null);
        this.eventService.sendEvent('navigate', 'auth/password/reset');
        break;
      }
      case 'login.password.reset': {
        await this.loginAsync(
          LoginType.PasswordReset,
          authData.mandator,
          this.currentUser.userName,
          authData.password
        );
        break;
      }
      case 'logout': {
        this.store.dispatch(signedInUserActions.logout());

        this._currentUser = null;
        this._currentUser$.next(null);

        this.authenticationStatus = AuthenticationStatus.LoggedOut;
        this.authenticationStatus$.next(AuthenticationStatus.LoggedOut);

        await this.setCurrentUser(null);
        this.eventService.sendEvent('navigate', 'auth/login');
        break;
      }
    }
  }
}
